import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Target, Clock, CheckCircle2, XCircle, RotateCcw, LayoutDashboard } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";

function fmtTime(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m} min ${sec} sec`;
}

export default function Result() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryLoading, setRetryLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/student/attempts/${attemptId}`);
        setDetail(data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        navigate("/student", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [attemptId, navigate]);

  const retry = async () => {
    if (!detail) return;
    setRetryLoading(true);
    try {
      const { data } = await api.post("/student/attempts", { exercise_id: detail.exercise.id });
      navigate(`/practice/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setRetryLoading(false);
    }
  };

  if (loading || !detail) {
    return <div className="min-h-screen flex items-center justify-center bg-canvas text-stone">Loading…</div>;
  }
  const attempt = detail.attempt;
  const questions = detail.questions; // includes correct_answer only when reveal is allowed
  const exercise = detail.exercise;
  const isAssessment = exercise?.type === "assessment";
  const revealAnswers = !isAssessment || exercise?.reveal_answers;
  const answersMap = Object.fromEntries((attempt.answers || []).map((a) => [a.question_id, a]));
  const attemptsUsed = exercise?.attempts_used ?? 0;
  const maxAttempts = exercise?.max_attempts ?? 3;
  const attemptsLeft = Math.max(0, maxAttempts - attemptsUsed);

  return (
    <div className="min-h-screen bg-canvas">
      <header className="border-b border-cloud bg-white/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <button
            data-testid="back-to-dashboard-header"
            onClick={() => navigate("/student")}
            className="text-sm font-bold text-stone hover:text-ink"
          >
            Dashboard
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-lemon/40 mb-4 animate-pop">
            <Trophy className="w-10 h-10 text-watermelon" />
          </div>
          <h1 data-testid="result-title" className="font-heading text-4xl sm:text-5xl font-bold text-ink">
            {isAssessment ? "Exam attempt complete!" : "Practice Complete!"}
          </h1>
          <p className="text-lg text-stone mt-2">{detail.exercise.title}</p>
          {isAssessment && (
            <p data-testid="assessment-attempts-status" className="mt-2 text-sm font-bold text-ink inline-block px-3 py-1 rounded-full bg-lemon/40">
              Attempt {attemptsUsed} of {maxAttempts} · {attemptsLeft} left
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-10">
          <div className="p-6 bg-white rounded-3xl card-tactile text-center">
            <div className="text-xs font-bold uppercase tracking-wider text-pebble mb-1">Score</div>
            <div data-testid="result-score" className="font-heading text-5xl font-bold text-ink">
              {attempt.score}<span className="text-2xl text-stone">/{attempt.total_questions}</span>
            </div>
            <Trophy className="w-6 h-6 text-lemon mx-auto mt-2" />
          </div>
          <div className="p-6 bg-white rounded-3xl card-tactile text-center">
            <div className="text-xs font-bold uppercase tracking-wider text-pebble mb-1">Accuracy</div>
            <div data-testid="result-accuracy" className="font-heading text-5xl font-bold text-leaf">
              {attempt.accuracy}<span className="text-2xl">%</span>
            </div>
            <Target className="w-6 h-6 text-leaf mx-auto mt-2" />
          </div>
          <div className="p-6 bg-white rounded-3xl card-tactile text-center">
            <div className="text-xs font-bold uppercase tracking-wider text-pebble mb-1">Time</div>
            <div data-testid="result-time" className="font-heading text-3xl sm:text-4xl font-bold text-sky">
              {fmtTime(attempt.time_taken_seconds)}
            </div>
            <Clock className="w-6 h-6 text-sky mx-auto mt-2" />
          </div>
        </div>

        {/* Review */}
        {revealAnswers ? (
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-bold text-ink mb-4">Question review</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {questions.map((q, i) => {
                const ans = answersMap[q.id];
                const stAns = ans?.answer;
                const isSkipped = !!ans?.is_skipped;
                const isCorrect = !isSkipped && stAns === q.correct_answer;
                const borderClr = isCorrect ? "border-leaf" : isSkipped ? "border-lemon" : "border-watermelon";
                return (
                  <div
                    key={q.id}
                    data-testid={`review-item-${i + 1}`}
                    className={`p-5 rounded-3xl card-tactile bg-white border-l-4 ${borderClr}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="text-xs font-bold uppercase tracking-wider text-pebble">Question {i + 1}</div>
                      {isCorrect ? (
                        <span className="inline-flex items-center gap-1 text-leaf font-bold text-xs"><CheckCircle2 className="w-4 h-4" /> Correct</span>
                      ) : isSkipped ? (
                        <span className="inline-flex items-center gap-1 text-ink font-bold text-xs bg-lemon/40 px-2 py-0.5 rounded-full">Time up</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-watermelon font-bold text-xs"><XCircle className="w-4 h-4" /> Incorrect</span>
                      )}
                    </div>
                    <div className="font-heading text-lg font-semibold text-ink">
                      {q.starting_number}
                      {q.operations.map((op, j) => (
                        <span key={j}> <span className={op.op === "+" ? "text-leaf" : op.op === "-" ? "text-watermelon" : op.op === "×" ? "text-sky" : "text-sky-hover"}>{op.op}</span>{op.value}</span>
                      ))}
                      <span className="text-stone"> = </span>
                      <span>{q.correct_answer}</span>
                    </div>
                    <div className="text-sm text-stone mt-2">
                      Your answer: <span className={`font-bold ${isCorrect ? "text-leaf" : isSkipped ? "text-ink" : "text-watermelon"}`}>
                        {isSkipped ? "skipped" : (stAns != null ? stAns : "—")}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ) : (
          <section className="mb-10" data-testid="review-hidden">
            <div className="p-6 bg-white rounded-3xl card-tactile text-center">
              <Trophy className="w-10 h-10 text-lemon mx-auto mb-3" />
              <div className="font-heading text-xl font-bold text-ink">Answers are hidden</div>
              <p className="text-stone text-sm mt-2">
                You'll see the correct answers after using all {maxAttempts} attempts. Best of luck on your next try!
              </p>
            </div>
          </section>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {(!isAssessment || attemptsLeft > 0) && (
            <button
              data-testid="try-again-button"
              onClick={retry}
              disabled={retryLoading}
              className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-watermelon text-white font-heading font-bold text-lg btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
            >
              <RotateCcw className="w-5 h-5" /> {retryLoading ? "Starting…" : (isAssessment ? `Try Again (${attemptsLeft} left)` : "Try Again")}
            </button>
          )}
          <button
            data-testid="back-to-dashboard-button"
            onClick={() => navigate("/student")}
            className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-white border-2 border-cloud text-ink font-heading font-bold text-lg btn-tactile hover:border-sky"
          >
            <LayoutDashboard className="w-5 h-5" /> Back to Dashboard
          </button>
        </div>
      </main>
    </div>
  );
}
